/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

// src/components/Modals/Firmalar/FirmaEkleModal.js

import React, { useState, useEffect } from "react";
import {
  Button, Modal, ModalOverlay, ModalContent, ModalCloseButton,
  FormControl, FormLabel, Input, Select, VStack, Text, useToast, Grid, GridItem, Box, HStack, FormHelperText, Switch
} from "@chakra-ui/react";
import api from "utils/api";
import { getCurrentUser } from "services/authService";

export default function FirmaEkleModal({
  isOpen,
  onClose,
  onSave,
  bayiler = [], // ✅ Parent'tan prop olarak al
}) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [otherSector, setOtherSector] = useState("");
  const [userRole, setUserRole] = useState(null);

  const initialForm = {
    firma_ismi: "",
    email: "",
    sabit_no: "",
    irtibat: "",
    vergi_no: "",
    adres: "",
    acik_adres: "",
    musteri_tipi: "",
    sektor: "",
    notlar: "",
    bayi_id: "",
    uetdsYetkilendirme: false,
  };

  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    getCurrentUser()
      .then((user) => {
        setUserRole(user.role);
      })
      .catch(() => {
        setUserRole(null);
      });
  }, []);

  const handleChange = (field, value) => {
    // Sektör Diğer'den farklı bir değere çekilirse, diğer alanını temizle
    if (field === "sektor" && value !== "Diğer") {
      setOtherSector("");
    }
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.firma_ismi || !form.email) {
      toast({
        title: "Eksik Bilgi",
        description: "Firma ismi ve e-posta adresi zorunludur",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    // Eğer sektör Diğer ise, kullanıcıdan özel bir değer alınmış olmalı
    if (form.sektor === "Diğer") {
      const trimmed = otherSector.trim();
      if (!trimmed) {
        toast({
          title: "Eksik Bilgi",
          description: "Lütfen Diğer sektör için bir değer girin",
          status: "warning",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
        return;
      }
    }

    setLoading(true);
    try {
      const sektorValue =
        form.sektor === "Diğer" ? otherSector.trim() : form.sektor || null;

      const payload = {
        firma_ismi: form.firma_ismi,
        email: form.email,
        sabit_no: form.sabit_no,
        irtibat: form.irtibat,
        vergi_no: form.vergi_no,
        adres: form.adres,
        acik_adres: form.acik_adres,
        musteri_tipi: form.musteri_tipi,
        sektor: sektorValue,
        notlar: form.notlar,
        firma_durumu: "aktif",
        kayit_tarihi: new Date().toISOString().split("T")[0],
        bayi_id: form.bayi_id ? parseInt(form.bayi_id) : null,
      };

      const response = await api.post("/api/firmalar", payload);

      if (!response.ok) {
        let errorMessage = `Firma kaydedilemedi: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          // JSON parse hatası, varsayılan mesajı kullan
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      const firmaId = data?.id ?? data?.HspId;

      let uetdsOk = null;
      if (userRole === "admin" && form.uetdsYetkilendirme && firmaId != null) {
        try {
          // Backend: GET user → uetds=true merge → PUT (partial gövde Traccar'da alan ezmez)
          const uetdsRes = await api.put(`/api/users/${firmaId}`, { uetds: true });
          if (!uetdsRes.ok) {
            let detail = `UETDS açılamadı: ${uetdsRes.status}`;
            try {
              const errBody = await uetdsRes.json();
              detail = errBody?.detail || errBody?.message || detail;
            } catch (_) {
              /* ignore */
            }
            throw new Error(detail);
          }
          uetdsOk = true;
        } catch (uetdsErr) {
          uetdsOk = false;
          toast({
            title: "Firma eklendi, UETDS açılamadı",
            description: uetdsErr.message || "Admin oturumu / Traccar hatası",
            status: "warning",
            duration: 6000,
            isClosable: true,
            position: "top-right",
          });
        }
      }

      toast({
        title: "Firma Eklendi",
        description:
          uetdsOk === true
            ? `${form.firma_ismi} kaydedildi; UETDS yetkilendirme açıldı`
            : `${form.firma_ismi} başarıyla kaydedildi`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      setForm(initialForm);
      onSave?.(data);
      onClose();
    } catch (err) {
      const errorMessage = err.message || "Bilinmeyen bir hata oluştu";
      toast({
        title: "Kayıt Hatası",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" boxShadow="xl" m={4} maxH="90vh" overflow="hidden" bg="white" p={1}>
        
        {/* Header */}
        <Box bg="white" p={4} borderBottom="1px solid" borderColor="gray.200">
          <ModalCloseButton size="sm" />
          <VStack spacing={1} align="start">
            <Text fontSize="lg" fontWeight="medium" color="gray.800">
              Yeni Firma Ekle
            </Text>
            <Text fontSize="xs" color="gray.600">
              Firma bilgilerini doldurun
            </Text>
          </VStack>
        </Box>

        <Box px={6} py={4} maxH="500px" overflowY="auto">
          <Grid templateColumns="repeat(2, 1fr)" gap={3} mb={3}>
            <FormControl isRequired>
              <FormLabel fontSize="xs" color="gray.600">Firma Adı</FormLabel>
              <Input
                variant="flushed"
                size="sm"
                placeholder="ABC Ltd. Şti."
                value={form.firma_ismi}
                onChange={(e) => handleChange("firma_ismi", e.target.value)}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="xs" color="gray.600">E-posta</FormLabel>
              <Input
                variant="flushed"
                size="sm"
                type="email"
                placeholder="info@firma.com"
                value={form.email}
                onChange={(e) => handleChange("email", e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="gray.600">Telefon</FormLabel>
              <Input
                variant="flushed"
                size="sm"
                placeholder="05xx xxx xx xx"
                value={form.irtibat}
                onChange={(e) => handleChange("irtibat", e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="gray.600">Sabit Telefon</FormLabel>
              <Input
                variant="flushed"
                size="sm"
                placeholder="0212 xxx xx xx"
                value={form.sabit_no}
                onChange={(e) => handleChange("sabit_no", e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="gray.600">Vergi No</FormLabel>
              <Input
                variant="flushed"
                size="sm"
                placeholder="1234567890"
                value={form.vergi_no}
                onChange={(e) => handleChange("vergi_no", e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="gray.600">Müşteri Tipi</FormLabel>
              <Select
                variant="flushed"
                size="sm"
                placeholder="Seçiniz"
                value={form.musteri_tipi}
                onChange={(e) => handleChange("musteri_tipi", e.target.value)}
              >
                <option value="Bireysel">Bireysel</option>
                <option value="Kurumsal">Kurumsal</option>
                <option value="Bayi">Bayi</option>
              </Select>
            </FormControl>

            <GridItem colSpan={2}>
              <FormControl>
                <FormLabel fontSize="xs" color="gray.600">Sektör</FormLabel>
                <Select
                  variant="flushed"
                  size="sm"
                  placeholder="Sektör seçiniz"
                  value={form.sektor}
                  onChange={(e) => handleChange("sektor", e.target.value)}
                >
                  <option value="Lojistik">Lojistik</option>
                  <option value="İnşaat">İnşaat</option>
                  <option value="Tarım">Tarım</option>
                  <option value="Turizm">Turizm</option>
                  <option value="Hizmet">Hizmet</option>
                  <option value="Araç Kiralama">Araç Kiralama</option>
                  <option value="Diğer">Diğer</option>
                </Select>
              </FormControl>
            </GridItem>

            {form.sektor === "Diğer" && (
              <GridItem colSpan={2}>
                <FormControl isRequired>
                  <FormLabel fontSize="xs" color="gray.600">
                    Diğer Sektör
                  </FormLabel>
                  <Input
                    variant="flushed"
                    size="sm"
                    placeholder="Örn: Akaryakıt, Eğitim, vb."
                    value={otherSector}
                    onChange={(e) => setOtherSector(e.target.value)}
                  />
                </FormControl>
              </GridItem>
            )}

            <GridItem colSpan={2}>
              <FormControl>
                <FormLabel fontSize="xs" color="gray.600">Adres</FormLabel>
                <Input
                  variant="flushed"
                  size="sm"
                  placeholder="İl, ilçe bilgisi"
                  value={form.adres}
                  onChange={(e) => handleChange("adres", e.target.value)}
                />
              </FormControl>
            </GridItem>

            <GridItem colSpan={2}>
              <FormControl>
                <FormLabel fontSize="xs" color="gray.600">Detaylı Adres</FormLabel>
                <Input
                  variant="flushed"
                  size="sm"
                  placeholder="Detaylı adres bilgisi"
                  value={form.acik_adres}
                  onChange={(e) => handleChange("acik_adres", e.target.value)}
                />
              </FormControl>
            </GridItem>

            {userRole === "admin" && (
              <GridItem colSpan={2}>
                <FormControl>
                  <FormLabel fontSize="xs" color="gray.600">Bayi (Opsiyonel)</FormLabel>
                  <Select
                    variant="flushed"
                    size="sm"
                    placeholder="Bayi seçiniz (boş bırakılırsa admin'e eklenir)"
                    value={form.bayi_id}
                    onChange={(e) => handleChange("bayi_id", e.target.value)}
                  >
                    {bayiler.map((bayi) => (
                      <option key={bayi.id} value={bayi.id}>
                        {bayi.name}
                      </option>
                    ))}
                  </Select>
                  <FormHelperText fontSize="10px" color="gray.500">
                    Firma kaydını bir bayiye atamak için seçiniz. Boş bırakılırsa admin'e eklenir.
                  </FormHelperText>
                </FormControl>
              </GridItem>
            )}

            {userRole === "admin" && (
              <GridItem colSpan={2}>
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <FormLabel fontSize="sm" color="gray.700" mb={0} fontWeight="medium">
                      UETDS Yetkilendirme
                    </FormLabel>
                    <FormHelperText fontSize="10px" color="gray.500" mt={1} mb={0}>
                      Açıkken hesapta user.uetds=true yapılır (admin API; müşteri sonra credential girebilir).
                    </FormHelperText>
                  </Box>
                  <Switch
                    colorScheme="green"
                    isChecked={form.uetdsYetkilendirme}
                    onChange={(e) => handleChange("uetdsYetkilendirme", e.target.checked)}
                  />
                </FormControl>
              </GridItem>
            )}

            <GridItem colSpan={2}>
              <FormControl>
                <FormLabel fontSize="xs" color="gray.600">Notlar</FormLabel>
                <Input
                  variant="flushed"
                  size="sm"
                  placeholder="İlave notlar"
                  value={form.notlar}
                  onChange={(e) => handleChange("notlar", e.target.value)}
                />
              </FormControl>
            </GridItem>
          </Grid>

        </Box>

        {/* Footer */}
        <Box bg="white" px={6} py={4} borderTop="1px solid" borderColor="gray.200">
          <HStack spacing={3} justify="end">
            <Button 
              size="sm"
              variant="ghost" 
              onClick={onClose}
            >
              İptal
            </Button>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={handleSave}
              isLoading={loading}
              loadingText="Kaydediliyor..."
            >
              Firma Ekle
            </Button>
          </HStack>
        </Box>
      </ModalContent>
    </Modal>
  );
}